/** Match the modifier accepted by the application keyboard handlers. */
export function shortcut(key: string, platform = navigator.platform): string {
  return /Mac|iPhone|iPad|iPod/i.test(platform)
    ? `⌘${key.toUpperCase()}`
    : `Ctrl+${key.toUpperCase()}`
}

const TYPING = 'input, textarea, select, [contenteditable="true"]'
const PRIMARY_INPUT = '[data-primary-input]:not([disabled]), .vk-composer__input:not([disabled])'

/** True while the key event targets something the user is typing into. */
export function isTyping(e: KeyboardEvent): boolean {
  return !!(e.target as HTMLElement | null)?.closest?.(TYPING)
}

/**
 * "/" focuses the page's primary text input (message box, bark box). Ignored while typing, with
 * modifiers, or when the page has no such input. Returns whether it handled the key.
 */
export function focusPrimaryInputOnSlash(e: KeyboardEvent, root: ParentNode = document): boolean {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || isTyping(e)) return false
  const input = root.querySelector<HTMLElement>(PRIMARY_INPUT)
  if (!input) return false
  e.preventDefault()
  input.focus()
  return true
}

// ---- lanes-shell: project tab bindings ----
/** Installed PWA / desktop shell: the app may capture ⌘/Ctrl+digit (a browser tab reserves it). */
export function isStandalone(
  mm: ((q: string) => { matches: boolean }) | undefined = globalThis.matchMedia,
): boolean {
  try {
    return !!mm?.('(display-mode: standalone)').matches
  } catch {
    return false
  }
}

const isMac = (platform: string) => /Mac|iPhone|iPad|iPod/i.test(platform)

/** The binding that switches to tab `n` (1–9) in this context, as shown in tooltips. */
export function tabShortcut(n: number, standalone: boolean, platform = navigator.platform): string {
  if (standalone) return isMac(platform) ? `⌘${n}` : `Ctrl+${n}`
  return isMac(platform) ? `⌥${n}` : `Alt+${n}`
}

/** aria-keyshortcuts value for tab `n`. */
export function tabKeyShortcuts(n: number, standalone: boolean): string {
  return standalone ? `Meta+${n} Control+${n} Alt+${n}` : `Alt+${n}`
}

type KeyLike = Pick<KeyboardEvent, 'key' | 'code' | 'metaKey' | 'ctrlKey' | 'altKey' | 'shiftKey'>

/**
 * 0-based tab index for ⌥1–9 everywhere and ⌘/Ctrl+1–9 when standalone; null otherwise. Uses
 * `code` because ⌥+digit types a symbol on macOS.
 */
export function tabIndexFromKey(e: KeyLike, standalone: boolean): number | null {
  if (e.shiftKey) return null
  const m = /^Digit([1-9])$/.exec(e.code) ?? /^([1-9])$/.exec(e.key)
  if (!m) return null
  const mods = [e.metaKey, e.ctrlKey, e.altKey].filter(Boolean).length
  if (mods !== 1) return null
  if (e.altKey || standalone) return Number(m[1]) - 1
  return null
}
// ---- end lanes-shell ----

// ---- ia-shell: tab 0 is Home (⌥0 in a browser tab, ⌘/Ctrl+0 installed) ----
export function homeTabKey(e: KeyLike, standalone: boolean): boolean {
  if (e.shiftKey || !(e.code === 'Digit0' || e.key === '0')) return false
  const mods = [e.metaKey, e.ctrlKey, e.altKey].filter(Boolean).length
  return mods === 1 && (e.altKey || standalone)
}
// ---- end ia-shell ----
